import { interpretAsDocumentId, isValidAutomergeUrl, parseAutomergeUrl, stringifyAutomergeUrl, } from "@automerge/automerge-repo";
import { request } from "./index.js";
import { forwardingProxy } from "./forwarding-proxy.js";
import { OverlayHandle } from "./overlay-handle.js";
// The only members whose behavior an overlay repo changes; everything else
// (create/create2/clone/delete, the EventEmitter surface, ...) forwards to the
// realm-local base repo.
const OVERLAY_REPO_OWNED = new Set([
    "baseRepo",
    "handles",
    "find",
    "findWithProgress",
    "create",
    "create2",
]);
/**
 * A realm-local {@link RepoLike} shim that makes document resolution
 * remappable across provider scopes (including iframes) without sending a live
 * `Repo`/`DocHandle` over the wire.
 *
 * `find`/`findWithProgress` dispatch a `repo:handle-descriptor` subscription for
 * the requested url and resolve the returned `cloneUrl ?? url` against the
 * realm-local `baseRepo`, then hand back an {@link OverlayHandle} that keeps
 * reporting the *original* url. Every other method (`create`, `create2`,
 * `clone`, `delete`, the EventEmitter surface, ...) forwards to `baseRepo`
 * unchanged — created docs need no remapping.
 */
export class OverlayRepo {
    /** The realm-local repo all resolution ultimately bottoms out in. */
    baseRepo;
    #element;
    // Keyed by the *presented* url (documentId + path + heads), not just the
    // documentId, so distinct sub-document paths under the same root document
    // don't collide.
    #wrapped = new Map();
    #inner = new Map();
    #resolving = new Map();
    constructor(baseRepo, element) {
        this.baseRepo = baseRepo;
        this.#element = element;
        // Forward everything but resolution to the realm-local repo.
        return forwardingProxy(this, baseRepo, OVERLAY_REPO_OWNED);
    }
    // Only already-wrapped handles are exposed; unknown ids fall through to
    // `find`/`findWithProgress` so consumers never see an un-wrapped handle.
    // `#wrapped` is keyed by presented url, so derive the documentId from each
    // handle; sub-handles of the same root collapse onto a single entry (the
    // `Record<DocumentId, ...>` shape can't represent paths).
    get handles() {
        const out = {};
        for (const wrapped of this.#wrapped.values()) {
            out[wrapped.documentId] = wrapped;
        }
        return out;
    }
    async find(id) {
        const presented = presentedUrlOf(id);
        const cached = this.#wrapped.get(presented);
        if (cached)
            return cached;
        const wrapped = await this.#resolve(presented);
        return wrapped;
    }
    // Mirrors the realm-local repo's progress but reports `loading` until the
    // descriptor has arrived and the backing handle is wrapped — otherwise
    // consumers would briefly observe the un-wrapped (un-remapped) handle.
    findWithProgress(id) {
        const self = this;
        const presented = presentedUrlOf(id);
        // The reported `documentId` is the root id — `DocumentId` can't encode a
        // path — but caches are keyed by the full presented url.
        const documentId = parseAutomergeUrl(presented).documentId;
        const wrappedPromise = this.#resolve(presented);
        wrappedPromise.catch(() => { });
        const peek = () => {
            const inner = self.#inner.get(presented);
            if (!inner)
                return { state: "loading", sources: {} };
            const innerState = inner.peek();
            if (innerState.state !== "ready")
                return innerState;
            const wrapped = self.#wrapped.get(presented);
            if (!wrapped) {
                return { state: "loading", sources: innerState.sources };
            }
            return {
                state: "ready",
                handle: wrapped,
                sources: innerState.sources,
            };
        };
        return {
            documentId,
            peek,
            subscribe: (callback) => {
                // `closed` covers the unsubscribe-before-resolve race: without it the
                // resolution settling later would still subscribe to the inner
                // progress (leaking that subscription forever) and invoke the
                // consumer's callback after it unsubscribed.
                let closed = false;
                let last = null;
                let unsubscribeInner = () => { };
                const dispatch = () => {
                    if (closed)
                        return;
                    const state = peek();
                    const sig = state.state === "failed"
                        ? `failed:${state.error.message}`
                        : state.state;
                    if (sig === last)
                        return;
                    last = sig;
                    callback(state);
                };
                wrappedPromise.then(() => {
                    if (closed)
                        return;
                    const inner = self.#inner.get(presented);
                    if (inner)
                        unsubscribeInner = inner.subscribe(dispatch);
                    dispatch();
                }, dispatch);
                return () => {
                    closed = true;
                    unsubscribeInner();
                };
            },
            whenReady: ({ signal } = {}) => {
                const handlePromise = wrappedPromise;
                if (signal?.aborted)
                    return Promise.reject(signal.reason);
                if (!signal)
                    return handlePromise;
                return new Promise((resolve, reject) => {
                    const onAbort = () => reject(signal.reason);
                    signal.addEventListener("abort", onAbort, { once: true });
                    handlePromise.then((handle) => {
                        signal.removeEventListener("abort", onAbort);
                        resolve(handle);
                    }, (err) => {
                        signal.removeEventListener("abort", onAbort);
                        reject(err);
                    });
                });
            },
            // Deprecated; kept for pre-`peek()` consumers.
            get state() {
                return peek().state;
            },
            get progress() {
                return self.#inner.get(presented)?.progress;
            },
            get error() {
                const s = peek();
                return s.state === "failed" ? s.error : undefined;
            },
        };
    }
    // Plain local creates need no remapping; declared (rather than forwarded) so
    // the class still satisfies `RepoLike`.
    create(initialValue) {
        return this.baseRepo.create(initialValue);
    }
    create2(initialValue) {
        return this.baseRepo.create2(initialValue);
    }
    // De-dupes concurrent resolutions of the same presented url: the first
    // caller dispatches the subscription, everyone else awaits the same promise.
    #resolve(presented) {
        const existing = this.#resolving.get(presented);
        if (existing)
            return existing;
        const promise = (async () => {
            // The descriptor channel remaps whole documents, so ask about the root
            // and reapply the original path/heads onto whatever clone comes back.
            // The clone is a fork of the root, so the same sub-tree exists in it.
            const { documentId, segments, heads } = parseAutomergeUrl(presented);
            const rootUrl = stringifyAutomergeUrl({ documentId });
            const descriptor = await request(this.#element, {
                type: "repo:handle-descriptor",
                url: rootUrl,
            });
            const backingRoot = descriptor.cloneUrl ?? descriptor.url;
            const backingUrl = stringifyAutomergeUrl({
                documentId: parseAutomergeUrl(backingRoot).documentId,
                segments,
                heads,
            });
            const inner = this.baseRepo.findWithProgress(backingUrl);
            this.#inner.set(presented, inner);
            const backing = await this.baseRepo.find(backingUrl);
            const wrapped = new OverlayHandle({
                presentedUrl: presented,
                backing,
            });
            this.#wrapped.set(presented, wrapped);
            return wrapped;
        })();
        // Only successful resolutions stay memoized. A rejection — typically
        // baseRepo.find reporting unavailable because the doc (or its keyhive
        // access) hasn't synced yet — is retryable, so caching it would pin every
        // future find() of this url to the same stale rejection and defeat the
        // views' "retry once access syncs" recovery. Evict so the next find()
        // re-runs the whole resolution. The identity check keeps a late-arriving
        // cleanup from clobbering a newer in-flight attempt.
        promise.catch(() => {
            if (this.#resolving.get(presented) === promise) {
                this.#resolving.delete(presented);
                this.#inner.delete(presented);
            }
        });
        this.#resolving.set(presented, promise);
        return promise;
    }
}
// Canonicalize any id to its *presented* url, preserving the path suffix
// (`/a/@0/b`) and any pinned heads so sub-document and view-scoped urls keep
// their identity through the overlay. Plain document ids carry no path.
function presentedUrlOf(id) {
    if (isValidAutomergeUrl(id)) {
        const { documentId, segments, heads } = parseAutomergeUrl(id);
        return stringifyAutomergeUrl({ documentId, segments, heads });
    }
    return stringifyAutomergeUrl({ documentId: interpretAsDocumentId(id) });
}
